import { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ResponsiveSankey } from "@nivo/sankey";
import { UserPlus, CalendarCheck, CreditCard } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, StatCard, AdminCard, PageLoader, timeAgo } from "@/admin/components";
import { formatINR } from "@/lib/format";

const nodeColor = (n) => (n.drop ? "#d64242" : n.id?.startsWith("src_") ? "#94a3b8" : "#2f8f5b");

function JourneySankey() {
  const [d, setD] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/funnel").then(({ data }) => setD(data)).catch(() => setD({ nodes: [], links: [], total: 0 }));
  }, []);
  return (
    <AdminCard className="mt-6 p-6" testid="overview-journey">
      <h3 className="text-[15px] font-semibold text-[#111]">Astrologer Journey</h3>
      <p className="mb-2 text-[12px] text-[#999]">Full lifecycle flow · drop-offs in red · snapshot of {d?.total ?? 0} astrologers</p>
      <div style={{ height: 460 }} data-testid="journey-sankey">
        {!d ? (
          <div className="flex h-full items-center justify-center text-[13px] text-[#999]">Loading…</div>
        ) : d.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[13px] text-[#999]">No astrologers yet.</div>
        ) : (
          <ResponsiveSankey
            data={{ nodes: d.nodes, links: d.links }}
            margin={{ top: 16, right: 180, bottom: 16, left: 130 }}
            align="justify"
            sort="input"
            colors={nodeColor}
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.35}
            nodeThickness={14}
            nodeSpacing={16}
            nodeBorderRadius={3}
            nodeBorderWidth={0}
            linkOpacity={0.4}
            linkHoverOthersOpacity={0.1}
            linkColor={{ from: "target.color" }}
            enableLinkGradient
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={8}
            label={(n) => `${n.label} · ${n.value}`}
            labelTextColor="#333"
            theme={{ fontSize: 11, labels: { text: { fontSize: 11 } } }}
          />
        )}
      </div>
    </AdminCard>
  );
}

const STAT_DEFS = [
  ["total_astrologers", "Total Astrologers"],
  ["onboarding_complete", "Onboarding Complete"],
  ["active_paid", "Active Paid"],
  ["free_tier", "Free Tier"],
  ["churned", "Churned"],
  ["mrr", "MRR"],
  ["total_bookings", "Total Bookings"],
];

const ACTIVITY_ICON = { signup: UserPlus, booking: CreditCard, default: CalendarCheck };

export default function Overview() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/overview");
    setData(data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <PageLoader />;

  const maxFunnel = Math.max(...data.funnel.map((f) => f.count), 1);

  return (
    <div data-testid="admin-overview-page">
      <PageHeader title="Overview" subtitle="Live platform metrics · refreshes every 60s" />

      <div className="grid grid-cols-4 gap-4">
        {STAT_DEFS.map(([key, label]) => {
          const s = data.stats[key];
          const value = key === "mrr" ? formatINR(s.value) : s.value;
          return <StatCard key={key} testid={`stat-${key}`} label={label} value={value} delta={s.delta} />;
        })}
      </div>

      <AdminCard className="mt-6 p-6" testid="overview-revenue-chart">
        <h3 className="text-[15px] font-semibold text-[#111]">Revenue · last 12 months</h3>
        <p className="mb-4 text-[12px] text-[#999]">Consultation payments collected (₹)</p>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.revenue_series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0ede8" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#999" }} tickLine={false} axisLine={{ stroke: "#e9e6e1" }} />
              <YAxis tick={{ fontSize: 11, fill: "#999" }} tickLine={false} axisLine={false} width={48} />
              <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e9e6e1", fontSize: 13 }} />
              <Line type="monotone" dataKey="amount" stroke="#111111" strokeWidth={2.5} dot={{ r: 3, fill: "#111" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </AdminCard>

      <JourneySankey />

      <AdminCard className="mt-6 p-6" testid="overview-funnel">
        <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Onboarding Funnel</h3>
        <div className="space-y-2.5">
          {data.funnel.map((f, i) => {
            const pct = Math.round((f.count / maxFunnel) * 100);
            const prev = i > 0 ? data.funnel[i - 1].count : f.count;
            const drop = prev > 0 ? Math.round(((prev - f.count) / prev) * 100) : 0;
            return (
              <div key={f.stage} className="flex items-center gap-3">
                <div className="w-44 shrink-0 text-[13px] text-[#555]">{f.stage}</div>
                <div className="relative h-8 flex-1 overflow-hidden rounded-[8px] bg-[#f2f0ec]">
                  <div className="flex h-full items-center rounded-[8px] bg-[#111111] px-3 text-[12px] font-semibold text-white" style={{ width: `${Math.max(pct, 8)}%` }}>
                    {f.count}
                  </div>
                </div>
                <div className="w-20 shrink-0 text-right text-[12px] text-[#999]">{i > 0 ? `-${drop}%` : ""}</div>
              </div>
            );
          })}
        </div>
      </AdminCard>

      <AdminCard className="mt-6 p-6" testid="overview-activity">
        <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Recent Activity</h3>
        {data.recent_activity.length === 0 ? (
          <p className="text-[14px] text-[#999]">No activity yet.</p>
        ) : (
          <div className="divide-y divide-[#f2efea]">
            {data.recent_activity.map((a, i) => {
              const Icon = ACTIVITY_ICON[a.type] || ACTIVITY_ICON.default;
              return (
                <div key={i} className="flex items-center gap-3 py-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f2f0ec] text-[#111]">
                    <Icon size={15} />
                  </div>
                  <p className="flex-1 text-[14px] text-[#222]">{a.description}</p>
                  <span className="text-[12px] text-[#999]">{timeAgo(a.timestamp)}</span>
                </div>
              );
            })}
          </div>
        )}
      </AdminCard>
    </div>
  );
}
